import { useState, type FC } from "react";
import type { IconButtonProps, SxProps, Theme } from "@mui/material";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { WhiteSpace } from "@privacy/ui";
import { MailIcon, PencilIcon, PhoneIcon, XIcon } from "./icons";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import * as FormDialog from "./FormDialog";
import z from "zod";

export type ContactPerson = {
  firstName?: string | undefined;
  lastName?: string | undefined;
  email?: string | undefined;
  phoneNumber?: string | undefined;
  title?: string | undefined;
};

const ACTION_BUTTONS_CLASS_NAME = "action-buttons";

export const ContactPersonInput: FC<{
  contactPerson: ContactPerson;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  onEdit: (updatedContactPerson: ContactPerson) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
}> = ({ contactPerson, sx, onEdit, onDelete, disabled }) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState<"edit" | "delete" | null>(null);

  return (
    <>
      {openDialog === "edit" && (
        <AddOrUpdateContactPersonDialog
          onClose={() => setOpenDialog(null)}
          onSubmit={onEdit}
          defaultContactPerson={contactPerson}
        />
      )}
      {openDialog === "delete" && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setOpenDialog(null)}
          onDelete={onDelete}
          headerText={t("ContactPerson.Delete.ConfirmTitle")}
          message={t("ContactPerson.Delete.ConfirmMessage")}
          confirmButtonLabel={t("ContactPerson.Delete.ConfirmSubmitButton")}
        />
      )}
      <Box
        sx={{
          "minHeight": "36px",
          "py": 1,
          "px": 2,
          "display": "flex",
          "alignItems": "center",
          "gap": 0.5,
          "justifyContent": "space-between",
          "&:hover": { backgroundColor: theme => (disabled ? "revert" : theme.palette.action.hover) },
          [`& .${ACTION_BUTTONS_CLASS_NAME}`]: { opacity: 0 },
          [`&:is(:hover, :focus-within) .${ACTION_BUTTONS_CLASS_NAME}`]: { opacity: 1 },
          "borderRadius": 1,
          ...sx,
        }}
      >
        <ContactPersonContent contactPerson={contactPerson} />
        {!disabled && (
          <Stack direction="row" className={ACTION_BUTTONS_CLASS_NAME} sx={{ gap: 0.5 }}>
            <ActionButton onClick={() => setOpenDialog("edit")} title={t("ContactPerson.Edit.Tooltip")}>
              <PencilIcon size={14} color={(theme: Theme) => theme.palette.text.secondary} />
            </ActionButton>
            <ActionButton onClick={() => setOpenDialog("delete")} title={t("ContactPerson.Delete.Tooltip")}>
              <XIcon color={(theme: Theme) => theme.palette.text.secondary} size={14} />
            </ActionButton>
          </Stack>
        )}
      </Box>
    </>
  );
};

const ActionButton = ({ title, ...props }: IconButtonProps) => {
  return (
    <Tooltip title={title}>
      <IconButton
        aria-label={title}
        size="small"
        sx={{ "&:hover": { backgroundColor: theme => theme.palette.grey["300"] } }}
        {...props}
      />
    </Tooltip>
  );
};

export const ContactPersonContent: FC<{
  contactPerson: ContactPerson;
  typographySx?: SxProps<Theme>;
}> = ({ contactPerson, typographySx }) => {
  return (
    <Stack
      direction="row"
      sx={{
        fontSize: theme => theme.typography.pxToRem(14),
        color: theme => theme.palette.text.secondary,
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        sx={[
          {
            color: "inherit",
            fontSize: theme => theme.typography.pxToRem(14),
            lineHeight: 1.5,
          },
          ...(Array.isArray(typographySx) ? typographySx : [typographySx]),
        ]}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", whiteSpace: "pre", mb: 0.5 }}>
          {contactPerson.firstName && (
            <Box component="span" sx={{ fontWeight: "600", color: theme => theme.palette.text.primary }}>
              {contactPerson.firstName}
            </Box>
          )}
          {contactPerson.lastName && (
            <>
              <Box component="span" sx={{ fontWeight: "600", color: theme => theme.palette.text.primary }}>
                {contactPerson.firstName && <WhiteSpace />}
                {contactPerson.lastName}
              </Box>
            </>
          )}
          {contactPerson.title && (
            <Box component="span">
              {(contactPerson.firstName || contactPerson.lastName) && (
                <>
                  <WhiteSpace />
                  <WhiteSpace />
                  |
                  <WhiteSpace />
                  <WhiteSpace />
                </>
              )}
              {contactPerson.title}
            </Box>
          )}
        </Box>
        {contactPerson.email && (
          <Box
            component="span"
            sx={{ gap: 1, display: "flex", fontSize: theme => theme.typography.pxToRem(12), alignItems: "center" }}
          >
            <MailIcon size={12} />
            {contactPerson.email}
            <br />
          </Box>
        )}
        {contactPerson.phoneNumber && (
          <Box
            component="span"
            sx={{ fontSize: theme => theme.typography.pxToRem(12), display: "flex", alignItems: "center", gap: 1 }}
          >
            <PhoneIcon size={12} />
            {contactPerson.phoneNumber}
          </Box>
        )}
      </Typography>
    </Stack>
  );
};

type AddOrUpdateContactPersonDialogProps = {
  onClose: () => void;
  onSubmit: (contactPerson: ContactPerson) => Promise<unknown>;
  defaultContactPerson?: ContactPerson;
};

const optionalEmailSchema = z.union([z.string().email(), z.literal("")]).optional();
const ONE_FIELD_REQUIRED = "ONE_FIELD_REQUIRED";

export const AddOrUpdateContactPersonDialog: FC<AddOrUpdateContactPersonDialogProps> = ({
  onClose,
  onSubmit,
  defaultContactPerson,
}) => {
  const { t } = useTranslation();
  const {
    control,
    setFocus,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<ContactPerson>({
    defaultValues: defaultContactPerson ?? { firstName: "", lastName: "", email: "", phoneNumber: "", title: "" },
  });
  const mutation = useMutation({ mutationFn: onSubmit });

  FormDialog.useAutoFocusField(setFocus, "firstName");

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>
        {defaultContactPerson ? t("ContactPerson.Dialog.EditTitle") : t("ContactPerson.Dialog.AddTitle")}
      </FormDialog.Title>
      <FormDialog.Form
        onSubmit={handleSubmit(data => {
          if (Object.values(data).every(value => !value)) return setError("root", { type: ONE_FIELD_REQUIRED });
          clearErrors("root");
          mutation.mutate(data, { onSuccess: onClose });
        })}
      >
        <FormDialog.Content>
          <FormDialog.FormError
            isError={Boolean(errors["root"] || mutation.isError)}
            errorMessage={
              errors["root"]?.type === ONE_FIELD_REQUIRED ? t("ContactPerson.Dialog.OneFieldRequired") : undefined
            }
          />
          <FormDialog.Fields>
            <Stack direction="row" sx={{ gap: 4 }}>
              <FormDialog.TextField
                control={control}
                name="firstName"
                label={t("ContactPerson.Dialog.FirstNameLabel")}
              />

              <FormDialog.TextField control={control} name="lastName" label={t("ContactPerson.Dialog.LastNameLabel")} />
            </Stack>

            <FormDialog.TextField control={control} name="title" label={t("ContactPerson.Dialog.TitleLabel")} />

            <FormDialog.TextField
              control={control}
              name="email"
              rules={{
                validate: value => optionalEmailSchema.safeParse(value).success || t("Input.Validation.RequiredEmail"),
              }}
              label={t("ContactPerson.Dialog.EmailLabel")}
            />

            <FormDialog.TextField
              control={control}
              name="phoneNumber"
              label={t("ContactPerson.Dialog.PhoneNumberLabel")}
              type="tel"
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={mutation.isPending}>
            {t(defaultContactPerson ? "ContactPerson.Dialog.SaveButton" : "ContactPerson.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
